import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Plus, User, Key, Search, Pencil, Trash2, 
  UserCheck, UserX, MoreVertical, Shield, Users, RefreshCw, Check, Lock
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from 'sonner';
import { useUser } from '@/utils/userHelper';

const ROLE_OPTIONS = [
  { value: 'hotel_admin', label: 'Hotel Admin', color: 'bg-purple-100 text-purple-700', description: 'Full hotel management' },
  { value: 'manager', label: 'Manager', color: 'bg-blue-100 text-blue-700', description: 'Operations management' },
  { value: 'front_office', label: 'Front Office', color: 'bg-green-100 text-green-700', description: 'Check-in/out, bookings' },
  { value: 'service_staff', label: 'Service Staff', color: 'bg-teal-100 text-teal-700', description: 'Tasks, requests, room service' },
  { value: 'restaurant_staff', label: 'Restaurant Staff', color: 'bg-amber-100 text-amber-700', description: 'POS, menu management' },
  { value: 'housekeeping', label: 'Housekeeping', color: 'bg-cyan-100 text-cyan-700', description: 'Room cleaning, maintenance' },
];

// Get available roles based on current user's role
const getAvailableRoles = (currentUserRole) => {
  // Hotel Admin cannot create Hotel Admin, Company Admin, Finance Admin, or Super Admin
  if (currentUserRole === 'hotel_admin') {
    return ROLE_OPTIONS.filter(role => role.value !== 'hotel_admin');
  }
  return ROLE_OPTIONS;
};

// Multi-select role component - extracted to avoid re-renders
const RoleMultiSelect = ({ selectedRoles, onToggle, disabled = false, currentUserRole }) => {
  const availableRoles = getAvailableRoles(currentUserRole);
  return (
    <div className="space-y-2">
      {availableRoles.map(role => {
        const isSelected = selectedRoles.includes(role.value);
        return (
          <div 
            key={role.value}
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
              isSelected 
                ? 'border-primary bg-primary/5' 
                : 'border-gray-200 hover:border-gray-300'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!disabled) onToggle(role.value);
            }}
          >
            <div className={`h-4 w-4 rounded border flex items-center justify-center ${
              isSelected ? 'bg-primary border-primary' : 'border-gray-300'
            }`}>
              {isSelected && <Check className="h-3 w-3 text-white" />}
            </div>
            <div className="flex-1">
              <p className="font-medium text-sm">{role.label}</p>
              <p className="text-xs text-muted-foreground">{role.description}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function UserManagement({ user: propUser, isTab, tabId }) {
  const user = useUser(propUser);

  const [users, setUsers] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [setPinDialogOpen, setSetPinDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pinData, setPinData] = useState({ pin: '', confirmPin: '' });
  
  // Form states - roles is now an array
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    roles: ['front_office'],
    hotel_id: user.hotel_id || ''
  });
  const [newPassword, setNewPassword] = useState('');
  const [changePasswordData, setChangePasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    loadUsers();
    if (user.role === 'super_admin') {
      loadHotels();
    }
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadHotels = async () => {
    try {
      const response = await api.get('/super-admin/hotels');
      setHotels(response.data);
    } catch (error) {
      console.error('Failed to load hotels');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      roles: ['front_office'],
      hotel_id: user.hotel_id || ''
    });
  };

  // Toggle role selection
  const toggleRole = (roleValue) => {
    setFormData(prev => {
      const currentRoles = prev.roles || [];
      if (currentRoles.includes(roleValue)) {
        // Remove role (but keep at least one)
        const newRoles = currentRoles.filter(r => r !== roleValue);
        return { ...prev, roles: newRoles.length > 0 ? newRoles : currentRoles };
      } else {
        // Add role
        return { ...prev, roles: [...currentRoles, roleValue] };
      }
    });
  };

  // Get user's roles as array (handle legacy single role)
  const getUserRoles = (u) => {
    if (u.roles && Array.isArray(u.roles)) return u.roles;
    if (u.role) return [u.role];
    return [];
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (formData.roles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }
    try {
      // Send both role (primary) and roles (all) for backward compatibility
      await api.post('/auth/register', {
        ...formData,
        role: formData.roles[0], // Primary role for backward compatibility
        roles: formData.roles
      });
      toast.success('User created successfully');
      setCreateDialogOpen(false);
      resetForm();
      loadUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create user'));
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (formData.roles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }
    try {
      await api.put(`/users/${selectedUser.id}`, {
        name: formData.name,
        email: formData.email,
        role: formData.roles[0], // Primary role
        roles: formData.roles
      });
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update user'));
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    try {
      await api.delete(`/users/${selectedUser.id}`);
      toast.success('User deleted successfully');
      setDeleteConfirmOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete user'));
    }
  };

  const handleToggleStatus = async (userToToggle) => {
    try {
      await api.patch(`/users/${userToToggle.id}/toggle`);
      toast.success(`User ${userToToggle.is_active ? 'deactivated' : 'activated'} successfully`);
      loadUsers();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSetPin = async () => {
    if (pinData.pin.length < 4 || pinData.pin.length > 6) {
      toast.error('PIN must be 4-6 digits');
      return;
    }
    if (!/^\d+$/.test(pinData.pin)) {
      toast.error('PIN must contain only digits');
      return;
    }
    if (pinData.pin !== pinData.confirmPin) {
      toast.error('PINs do not match');
      return;
    }
    
    try {
      await api.post('/pos/set-user-pin', {
        user_id: selectedUser.id,
        pin: pinData.pin
      });
      toast.success(`POS/KDS PIN set for ${selectedUser.name}`);
      setSetPinDialogOpen(false);
      setSelectedUser(null);
      setPinData({ pin: '', confirmPin: '' });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to set PIN'));
    }
  };

  const openSetPinDialog = (userToSet) => {
    setSelectedUser(userToSet);
    setPinData({ pin: '', confirmPin: '' });
    setSetPinDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.post(`/users/${selectedUser.id}/reset-password`, {
        user_id: selectedUser.id,
        new_password: newPassword
      });
      toast.success('Password reset successfully');
      setResetPasswordOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reset password'));
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (changePasswordData.new_password !== changePasswordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (changePasswordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.post('/auth/change-password', {
        old_password: changePasswordData.old_password,
        new_password: changePasswordData.new_password
      });
      toast.success('Password changed successfully');
      setChangePasswordOpen(false);
      setChangePasswordData({ old_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to change password'));
    }
  };

  const openEditDialog = (userToEdit) => {
    setSelectedUser(userToEdit);
    const userRoles = getUserRoles(userToEdit);
    setFormData({
      name: userToEdit.name,
      email: userToEdit.email,
      password: '',
      roles: userRoles.length > 0 ? userRoles : ['front_office'],
      hotel_id: userToEdit.hotel_id || ''
    });
    setEditDialogOpen(true);
  };

  const openResetPasswordDialog = (userToReset) => {
    setSelectedUser(userToReset);
    setNewPassword('');
    setResetPasswordOpen(true);
  };

  const openDeleteDialog = (userToDelete) => {
    setSelectedUser(userToDelete);
    setDeleteConfirmOpen(true);
  };

  const getRoleBadge = (role) => {
    const roleInfo = ROLE_OPTIONS.find(r => r.value === role);
    if (roleInfo) return roleInfo;
    if (role === 'super_admin') return { label: 'Super Admin', color: 'bg-red-100 text-red-700' };
    return { label: role?.replace('_', ' ') || 'Unknown', color: 'bg-gray-100 text-gray-700' };
  };

  // Filter users - check if user has any of the filtered roles
  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const userRoles = getUserRoles(u);
    const matchesRole = filterRole === 'all' || userRoles.includes(filterRole);
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && u.is_active !== false) ||
                         (filterStatus === 'inactive' && u.is_active === false);
    return matchesSearch && matchesRole && matchesStatus;
  });

  return (
    <div className="space-y-6" data-testid="user-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope' }}>User Management</h1>
          <p className="text-muted-foreground mt-1">Manage staff users, roles, and permissions</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setChangePasswordOpen(true)}
            data-testid="change-password-btn"
          >
            <Key className="h-4 w-4 mr-2" />
            Change My Password
          </Button>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <Button 
              variant="outline" 
              onClick={() => openSetPinDialog(user)}
              data-testid="set-my-pin-btn"
            >
              <Lock className="h-4 w-4 mr-2" />
              Set/Reset My PIN
            </Button>
            <DialogContent className="max-w-md" data-testid="create-user-dialog">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                {user.role === 'super_admin' && (
                  <div className="space-y-2">
                    <Label>Hotel</Label>
                    <Select onValueChange={(value) => setFormData({...formData, hotel_id: value})} required>
                      <SelectTrigger data-testid="user-hotel-select">
                        <SelectValue placeholder="Select hotel" />
                      </SelectTrigger>
                      <SelectContent>
                        {hotels.map(hotel => (
                          <SelectItem key={hotel.id} value={hotel.id}>
                            {hotel.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="Enter full name"
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    placeholder="Enter email address"
                    data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                    minLength={6}
                    placeholder="Minimum 6 characters"
                    data-testid="user-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Roles * <span className="text-xs text-muted-foreground">(Select one or more)</span></Label>
                  <RoleMultiSelect 
                    selectedRoles={formData.roles} 
                    onToggle={toggleRole}
                    currentUserRole={user?.role}
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="submit" className="flex-1" data-testid="submit-create-user">Create User</Button>
                  <Button type="button" variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm(); }}>Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.length}</p>
                <p className="text-xs text-muted-foreground">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center">
                <UserCheck className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.is_active !== false).length}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <Shield className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.filter(u => {
                  const roles = getUserRoles(u);
                  return roles.includes('manager') || roles.includes('hotel_admin');
                }).length}</p>
                <p className="text-xs text-muted-foreground">Managers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center">
                <UserX className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.is_active === false).length}</p>
                <p className="text-xs text-muted-foreground">Inactive</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-users-input"
                />
              </div>
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-[150px]" data-testid="filter-role">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {ROLE_OPTIONS.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[130px]" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadUsers} data-testid="refresh-users">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Users Grid/Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Users ({filteredUsers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="h-8 w-8 rounded-full border-4 border-primary border-t-transparent animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Loading users...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchTerm || filterRole !== 'all' || filterStatus !== 'all' 
                  ? 'No users match your filters' 
                  : 'No users yet'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">User</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Roles</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Created</th>
                    <th className="text-right p-3 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(u => {
                    const userRoles = getUserRoles(u);
                    const isCurrentUser = u.id === user.id;
                    const canManage = user.role === 'hotel_admin' || user.role === 'super_admin' || user.role === 'admin';
                    
                    return (
                      <tr key={u.id} className="border-b hover:bg-muted/50 transition-colors" data-testid={`user-row-${u.id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-sm font-medium text-primary">
                                {u.name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium">{u.name}</p>
                              <p className="text-sm text-muted-foreground">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {userRoles.map(role => {
                              const roleInfo = getRoleBadge(role);
                              return (
                                <Badge key={role} className={roleInfo.color} data-testid={`user-role-badge-${u.id}-${role}`}>
                                  {roleInfo.label}
                                </Badge>
                              );
                            })}
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge 
                            className={u.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
                            data-testid={`user-status-badge-${u.id}`}
                          >
                            {u.is_active !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <span className="text-sm text-muted-foreground">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          {canManage && !isCurrentUser ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`user-actions-${u.id}`}>
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => openEditDialog(u)} data-testid={`edit-user-${u.id}`}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit User
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openResetPasswordDialog(u)} data-testid={`reset-password-${u.id}`}>
                                  <Key className="h-4 w-4 mr-2" />
                                  Reset Password
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openSetPinDialog(u)} data-testid={`set-pin-${u.id}`}>
                                  <Lock className="h-4 w-4 mr-2" />
                                  Set/Reset PIN
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleToggleStatus(u)} data-testid={`toggle-status-${u.id}`}>
                                  {u.is_active !== false ? (
                                    <>
                                      <UserX className="h-4 w-4 mr-2" />
                                      Deactivate
                                    </>
                                  ) : (
                                    <>
                                      <UserCheck className="h-4 w-4 mr-2" />
                                      Activate
                                    </>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-red-600" 
                                  onClick={() => openDeleteDialog(u)}
                                  data-testid={`delete-user-${u.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete User
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : isCurrentUser ? (
                            <Badge variant="outline" className="text-xs">You</Badge>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                data-testid="edit-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                data-testid="edit-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Roles <span className="text-xs text-muted-foreground">(Select one or more)</span></Label>
              <RoleMultiSelect 
                selectedRoles={formData.roles} 
                onToggle={toggleRole}
                currentUserRole={user?.role}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1" data-testid="save-edit-user">Save Changes</Button>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent className="max-w-sm" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Reset password for <strong>{selectedUser?.name}</strong>
            </p>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                data-testid="new-password-input"
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={handleResetPassword} className="flex-1" data-testid="confirm-reset-password">
                Reset Password
              </Button>
              <Button variant="outline" onClick={() => setResetPasswordOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change My Password Dialog */}
      <Dialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen}>
        <DialogContent className="max-w-sm" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Change My Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input
                type="password"
                value={changePasswordData.old_password}
                onChange={(e) => setChangePasswordData({...changePasswordData, old_password: e.target.value})}
                required
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={changePasswordData.new_password}
                onChange={(e) => setChangePasswordData({...changePasswordData, new_password: e.target.value})}
                required
                minLength={6}
                placeholder="Minimum 6 characters"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={changePasswordData.confirm_password}
                onChange={(e) => setChangePasswordData({...changePasswordData, confirm_password: e.target.value})}
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="flex-1" data-testid="confirm-change-password">
                Change Password
              </Button>
              <Button type="button" variant="outline" onClick={() => {
                setChangePasswordOpen(false);
                setChangePasswordData({ old_password: '', new_password: '', confirm_password: '' });
              }}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete <strong>{selectedUser?.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button variant="destructive" onClick={handleDelete} className="flex-1" data-testid="confirm-delete-user">
                Delete User
              </Button>
              <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Set/Reset PIN Dialog */}
      <Dialog open={setPinDialogOpen} onOpenChange={setSetPinDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="set-pin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              Set/Reset PIN
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Set PIN for <strong>{selectedUser?.name}</strong>. This PIN will be used to unlock POS and Kitchen Display System.
            </p>
            <div className="space-y-2">
              <Label>New PIN (4-6 digits)</Label>
              <Input
                type="password"
                value={pinData.pin}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setPinData({...pinData, pin: val});
                }}
                placeholder="Enter 4-6 digit PIN"
                maxLength={6}
                data-testid="new-pin-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm PIN</Label>
              <Input
                type="password"
                value={pinData.confirmPin}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setPinData({...pinData, confirmPin: val});
                }}
                placeholder="Confirm PIN"
                maxLength={6}
                data-testid="confirm-pin-input"
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={handleSetPin} className="flex-1" data-testid="confirm-set-pin">
                <Lock className="h-4 w-4 mr-2" />
                Set PIN
              </Button>
              <Button variant="outline" onClick={() => {
                setSetPinDialogOpen(false);
                setPinData({ pin: '', confirmPin: '' });
              }}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
